import {inject, Injectable} from '@angular/core';
import {LoggableErrorEntityService} from './loggable-error-entity.service';
import {LogBook} from '../../models/error/LogBook';
import {ErrorFilter} from '../../models/filter/ErrorFilter';
import {forkJoin, map, Observable, of, switchMap} from 'rxjs';
import {WatcherHttpClient} from '../httpClient/WatcherHttpClient';
import {TenantService} from '../tenant/tenant.service';
import {LogBookAPI} from '../../api/models/error/LogBookApi';
import {PagedResultApi} from '../../api/models/PagedResultApi';
import {ErrorDetailPreviewApi} from '../../api/models/error/ErrorDetailPreviewApi';
import {ErrorDetailPreview} from '../../models/error/ErrorDetailPreview';
import {PagedResult} from '../../models/PagedResult';
import { HttpParams } from '@angular/common/http';
import { LogBookQueryFilter } from '../../models/filter/LogBookQueryFilter';

@Injectable({
    providedIn: 'root'
})
export class LogBookService extends LoggableErrorEntityService<LogBook> {
    private watcherHttpClient: WatcherHttpClient = inject(WatcherHttpClient);
    private tenantService: TenantService = inject(TenantService);

    public getAmountOfErrorItems(errorFilter: ErrorFilter): Observable<number> {
        return this.watcherHttpClient.get<number>("/logBook/$count" + this.createODataQueryForAllErrorItems(errorFilter))
    }

    protected getAllWithFilter(filter: string): Observable<LogBook[]> {
        return this.watcherHttpClient.get<LogBookAPI[]>("/logBook" + filter).pipe(
            switchMap((logBooks: LogBookAPI[]) => logBooks?.length > 0 ?
                forkJoin(logBooks.map(logBook => this.createLogBook(logBook))) : of([])
            )
        );
    }

    protected applyBaseErrorFiltersForAllErrorItems(errorFilter: ErrorFilter) {
        this.queryBuilder
            .addFilter("IsSolved eq false")
            .addFilter(`CreatedAt gt ${errorFilter.occurredTimeRange.from.toISOString()}`)
            .addFilter(`CreatedAt lt ${errorFilter.occurredTimeRange.to.toISOString()}`)
            .addOrderBy("ErrorPriority", "desc")
            .addOrderBy("CreatedAt", "desc");
    }

    protected createODataQueryForSpecificErrorItem(id: string): string {
        this.queryBuilder.addFilter("Id eq " + id);
        return this.queryBuilder.build();
    }

    private createLogBook(logBookAPI: LogBookAPI): Observable<LogBook> {
        return forkJoin({
            tenant: this.tenantService.getTenant(logBookAPI.tenantId),
            application: this.tenantService.getApplication(logBookAPI.applicationId, logBookAPI.tenantId)
        }).pipe(
            map(({tenant, application}) =>
                new LogBook(
                    logBookAPI.id,
                    tenant,
                    application,
                    logBookAPI.createdAt,
                    logBookAPI.totalAmountOfErrors,
                    logBookAPI.runId,
                    logBookAPI.errorPriority,
                    logBookAPI.isSolved,
                )
            )
        );
    }

    public getAllErrorsFromLogBook(logBookId: string, logBookQueryFilter: LogBookQueryFilter): Observable<PagedResult<ErrorDetailPreview>> {
        let params = new HttpParams();
        
        const skip = logBookQueryFilter.pageIndex * logBookQueryFilter.pageSize;
        params = params
            .set("take", logBookQueryFilter.pageSize)
            .set("skip", skip);
        
        const trimmedSearchTerm = logBookQueryFilter.logBookErrorFilter.searchTerm.trim()
        if (trimmedSearchTerm)
            params = params.set("searchTerm", trimmedSearchTerm);

        return this.watcherHttpClient.get<PagedResultApi<ErrorDetailPreviewApi>>(`/logBook/${logBookId}/errors?${params}`).pipe(
            map((pagedResult: PagedResultApi<ErrorDetailPreviewApi>) =>  {
                const previews = pagedResult.items.length > 0 ? pagedResult.items.map(item => this.createErrorDetailPreview(item)) : [];
                return new PagedResult(
                    previews,
                    pagedResult.totalCount,
                    pagedResult.pageNumber,
                    pagedResult.pageSize
                );
            })
        );
    }

    public solveAllErrorsFromLogBook(logBookId: string): Observable<unknown> {
        return this.watcherHttpClient.post(`/logbook/${logBookId}/errors/solve`, null);
    }

    private createErrorDetailPreview(viewModel: ErrorDetailPreviewApi) {
        return new ErrorDetailPreview(
          viewModel.id,
          viewModel.isSolved,
          viewModel.priority,
          viewModel.content,
          viewModel.firstOccurred,
          viewModel.lastOccurred,
          viewModel.type
        );
    }
}
